import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { ROUTES_CONFIG } from "@/config/routes.config"
import {
    Card,
    CardHeader,
    CardFooter,
    CardTitle,
    CardAction,
    CardDescription,
    CardContent,
}
    from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Link } from 'react-router-dom';
import * as Icon from 'lucide-react'
import { useNavigate } from "react-router-dom"

type TeamInfo = {
  id: string
  name: string
  badgeUrl?: string
  formation?: string
  score?: number | undefined
}

export default function MatchHeader(props: {
  dateLabel?: string
  timeLabel?: string
  competition?: string
  status?: "pre" | "live" | "ft"
  home: TeamInfo
  away: TeamInfo
}) {
  const {
    dateLabel,
    timeLabel,
    competition,
    status = "pre",
    home,
    away,
  } = props

  const showScore =
    typeof home.score === "number" && typeof away.score === "number"

  const statusLabel =
    status === "live" ? "LIVE" : status === "ft" ? "FT" : `${dateLabel} • ${timeLabel}`

  const statusClasses =
    status === "live"
      ? "border-red-400/30 bg-red-500/15 text-red-300 animate-pulse"
      : "border-foreground/10 bg-foreground/5 text-foreground/80"

  return (
    <Card className="m-3 min-h-[220px] relative overflow-hidden rounded-2xl">
      <div className="pointer-events-none absolute inset-0 opacity-70 [mask-image:radial-gradient(70%_100%_at_50%_0%,black,transparent)]" />
      <CardHeader className="relative flex w-full items-center justify-center min-h-[30px]">
        <Link
          to={ROUTES_CONFIG.APP.MATCHES.$buildPath({})}
          className="absolute left-0 text-sm font-medium text-foreground/50 hover:text-foreground hover:underline w-fit flex items-center gap-2 ml-4"
        >
          <Icon.ArrowLeft className="w-4 h-4" />
          Terug
        </Link>
      </CardHeader>

      <CardContent className="relative">
        <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-6">
          <TeamBlock side="left" {...home} />

          <div className="relative flex flex-col items-center justify-center gap-3 text-center">
            <div className="relative flex items-center justify-center">
              <span className="mx-3 select-none text-xs sm:text-sm uppercase tracking-[0.4em] text-foreground/60">
                VS
              </span>
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-[140%] w-px -translate-x-1/2 -translate-y-1/2 bg-gradient-to-b from-transparent via-foreground/20 to-transparent" />
            </div>

            {showScore ? (
              <div className="flex items-center gap-2">
                <ScorePill value={home.score!} />
                <span className="text-lg text-foreground/60">:</span>
                <ScorePill value={away.score!} />
              </div>
            ) : (
              <div className="text-xl font-semibold">{timeLabel}</div>
            )}

            <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
              <Badge variant="outline" className={statusClasses}>
                {statusLabel}
              </Badge>
              {competition && (
                <Badge variant="outline" className="border-foreground/10 bg-foreground/5">
                  {competition}
                </Badge>
              )}
            </div>
          </div>
          <TeamBlock side="right" {...away} />
        </div>
      </CardContent>
    </Card>
  )
}

function TeamBlock({
  side = "left",
  id,
  name,
  badgeUrl,
  formation,
}: TeamInfo & { side?: "left" | "right" }) {
  const left = side === "left"
  const navigate = useNavigate()

  return (
    <div className={`flex items-center gap-4 ${left ? "sm:justify-start ml-10" : "sm:justify-end mr-10"}`}>
      {left && (
        <Avatar className="h-20 w-20 shrink-0 rounded-xl cursor-pointer hover:scale-105 ring-1 ring-foreground/10 bg-foreground/5" onClick={() => navigate(ROUTES_CONFIG.APP.CLUB_DETAIL.$buildPath({ params: { club_id: id } }))}>
          <AvatarImage className="object-contain" src={badgeUrl || ""} alt={`${name} badge`} />
          <AvatarFallback className="bg-foreground/10">-</AvatarFallback>
        </Avatar>
      )}

      <div className={`min-w-0 ${left ? "text-left" : "text-right"}`}>
        <h2 className="truncate cursor-pointer hover:underline text-2xl sm:text-3xl font-semibold tracking-tight" onClick={() => navigate(ROUTES_CONFIG.APP.CLUB_DETAIL.$buildPath({ params: { club_id: id } }))}>
          {name}
        </h2>
        <div className={`mt-1 flex items-center gap-2 text-sm ${left ? "" : "justify-end"}`}>
          <Badge variant="outline" className="border-emerald-400/30 bg-emerald-500/10 text-emerald-300">
            {formation ?? "—"}
          </Badge>
        </div>
      </div>

      {!left && (
        <Avatar className="h-20 w-20 shrink-0 rounded-xl cursor-pointer hover:scale-105 ring-1 ring-foreground/10 bg-foreground/5" onClick={() => navigate(ROUTES_CONFIG.APP.CLUB_DETAIL.$buildPath({ params: { club_id: id } }))}>
          <AvatarImage className="object-contain" src={badgeUrl || ""} alt={`${name} badge`} />
          <AvatarFallback className="bg-foreground/10">-</AvatarFallback>
        </Avatar>
      )}
    </div>
  )
}

function ScorePill({ value }: { value: number }) {
  return (
    <div className="min-w-[3.25rem] rounded-xl border border-foreground/10 bg-muted/60 px-3 py-2 text-center text-xl font-bold shadow-inner">
      {value}
    </div>
  )
}
